import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
} from '@mui/material';
import {
  People as PeopleIcon,
  Restaurant as RestaurantIcon,
  TrendingUp as TrendingUpIcon,
  ChevronRight as ChevronRightIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { AuthContext } from '../../context/AuthContext';
import { useIsMobile } from '../../hooks';
import { MobileList } from '../../components/common';
import api from '../../services/api';
import { colors, grey, primary, success, warning, error as errorColor, info } from '../../styles/tokens';

/**
 * Dashboard Admin
 */
const AdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentCoaches, setRecentCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const coachesResponse = await api.get('/admin/coaches?limit=5');
      const coaches = coachesResponse.coaches || [];
      const pagination = coachesResponse.pagination || {};

      const statsData = {
        totalCoaches: pagination.total || coaches.length,
        byStatus: {
          active: coaches.filter(c => c.tenantStatus === 'active').length,
          suspended: coaches.filter(c => c.tenantStatus === 'suspended').length,
        },
        byPlan: {
          starter: coaches.filter(c => c.subscriptionPlan === 'starter').length,
          pro: coaches.filter(c => c.subscriptionPlan === 'pro').length,
          enterprise: coaches.filter(c => c.subscriptionPlan === 'enterprise').length,
        },
      };

      setStats(statsData);
      setRecentCoaches(coaches.slice(0, 5));
    } catch (err) {
      console.error('Erreur lors du chargement du dashboard:', err);
      setError('Impossible de charger les donnees du dashboard.');
    } finally {
      setLoading(false);
    }
  };

  // StatCard component - responsive pour mobile
  const StatCard = ({ title, value, icon: Icon, bgColor, iconColor, subtitle }) => (
    <Card>
      <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: isMobile ? '0.625rem' : '0.6875rem',
                fontWeight: 600,
                color: grey[500],
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 0.5,
                // Permet le retour a la ligne sur mobile
                whiteSpace: isMobile ? 'normal' : 'nowrap',
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>
            <Typography sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: 700, color: grey[800] }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: '0.75rem', color: grey[500], mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              width: isMobile ? 32 : 40,
              height: isMobile ? 32 : 40,
              bgcolor: bgColor,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: isMobile ? 16 : 20, color: iconColor }} />
          </Avatar>
        </Stack>
      </CardContent>
    </Card>
  );

  // Chip styles
  const getPlanChipProps = (plan) => {
    const configs = {
      starter: { label: 'starter', sx: { bgcolor: grey[100], color: grey[700] } },
      pro: { label: 'pro', sx: { bgcolor: primary[50], color: primary[600] } },
      enterprise: { label: 'enterprise', sx: { bgcolor: success[50], color: success.dark } },
    };
    return configs[plan] || { label: plan || 'N/A', sx: { bgcolor: grey[100], color: grey[600] } };
  };

  const getStatusChipProps = (status) => {
    const configs = {
      active: { label: 'active', sx: { bgcolor: success[50], color: success.dark } },
      suspended: { label: 'suspended', sx: { bgcolor: errorColor[50], color: errorColor.dark } },
      pending: { label: 'pending', sx: { bgcolor: warning[50], color: warning.dark } },
      deleted: { label: 'deleted', sx: { bgcolor: grey[100], color: grey[600] } },
    };
    return configs[status] || { label: status || 'N/A', sx: { bgcolor: grey[100], color: grey[600] } };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard - Admin Yoco</title>
      </Helmet>

      <Box>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h1">Dashboard</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Bienvenue, {user?.firstName}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Stats - 2 colonnes sur mobile, 4 sur desktop */}
        <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mb: 3 }}>
          <Grid item xs={6} lg={3}>
            <StatCard
              title="Total Coachs"
              value={stats?.totalCoaches || 0}
              icon={PeopleIcon}
              bgColor={primary[50]}
              iconColor={primary[500]}
            />
          </Grid>
          <Grid item xs={6} lg={3}>
            <StatCard
              title="Coachs Actifs"
              value={stats?.byStatus?.active || 0}
              icon={TrendingUpIcon}
              bgColor={success[50]}
              iconColor={success.main}
            />
          </Grid>
          <Grid item xs={6} lg={3}>
            <StatCard
              title="Abonnement Pro"
              value={stats?.byPlan?.pro || 0}
              icon={StorageIcon}
              bgColor={info[50]}
              iconColor={info.main}
            />
          </Grid>
          <Grid item xs={6} lg={3}>
            <StatCard
              title="Abonnement Starter"
              value={stats?.byPlan?.starter || 0}
              icon={RestaurantIcon}
              bgColor={warning[50]}
              iconColor={warning.main}
            />
          </Grid>
        </Grid>

        {/* Recent Coaches */}
        <Card sx={{ mb: 3 }} className={isMobile ? 'mobile-list-container-with-header' : ''}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${colors.border}` }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h3">Derniers coachs inscrits</Typography>
              <Button
                component={Link}
                to="/admin/coaches"
                size="small"
                endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
              >
                Voir tous
              </Button>
            </Stack>
          </Box>

          {recentCoaches.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2">
                Aucun coach inscrit pour le moment.
              </Typography>
            </Box>
          ) : isMobile ? (
            /* Mobile: Liste en cards */
            <Box sx={{ px: 2, pt: 0, pb: 1 }}>
              <MobileList
                items={recentCoaches}
                renderItem={(coach) => {
                  const planProps = getPlanChipProps(coach.subscriptionPlan);
                  const statusProps = getStatusChipProps(coach.tenantStatus);
                  return {
                    primary: `${coach.firstName} ${coach.lastName}`,
                    secondary: coach.email,
                    meta: new Date(coach.createdAt).toLocaleDateString('fr-FR'),
                    chips: [
                      { label: planProps.label, sx: planProps.sx },
                      { label: statusProps.label, sx: statusProps.sx },
                    ],
                    onClick: () => navigate(`/admin/coaches/${coach._id}`),
                  };
                }}
                emptyMessage="Aucun coach inscrit"
              />
            </Box>
          ) : (
            /* Desktop: Table classique */
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Coach</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Inscription</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentCoaches.map((coach) => {
                    const planProps = getPlanChipProps(coach.subscriptionPlan);
                    const statusProps = getStatusChipProps(coach.tenantStatus);
                    return (
                      <TableRow key={coach._id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {coach.firstName} {coach.lastName}
                            </Typography>
                            <Typography variant="caption">
                              {coach.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={planProps.label}
                            size="small"
                            sx={planProps.sx}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusProps.label}
                            size="small"
                            sx={statusProps.sx}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">
                            {new Date(coach.createdAt).toLocaleDateString('fr-FR')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        {/* Quick Actions */}
        <Grid container spacing={isMobile ? 1.5 : 2}>
          <Grid item xs={6}>
            <Card
              component={Link}
              to="/admin/coaches"
              sx={{
                textDecoration: 'none',
                display: 'block',
                '&:hover': {
                  borderColor: colors.borderStrong,
                },
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: primary[50] }}>
                    <PeopleIcon sx={{ fontSize: 18, color: primary[500] }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h4">Gerer les Coachs</Typography>
                    <Typography variant="caption">Voir, modifier, suspendre</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card
              component={Link}
              to="/admin/recipes"
              sx={{
                textDecoration: 'none',
                display: 'block',
                '&:hover': {
                  borderColor: colors.borderStrong,
                },
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: warning[50] }}>
                    <RestaurantIcon sx={{ fontSize: 18, color: warning.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h4">Recettes Globales</Typography>
                    <Typography variant="caption">Bibliotheque partagee</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default AdminDashboard;
